import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { useGetConversationHistory } from '../hooks';
import { QueryMessageInput } from './message-input/query-message-input';
import { QueryMessageList } from './message-list/query-message-list';
import { isEmpty, isNil } from 'lodash';
import { CircularProgress } from '@mui/material';
import { EmptyStateView } from '@zonofi/seller-hub/components';
import { SVGS } from '@zonofi/seller-hub/assets';
import {
  getMessageListItemKey,
  isMessageEnvelopeItem,
  isQuerySeparatorItem,
} from '@zonofi/common/functions/message-list-helpers';
import './query-conversation-card.scss';

export const QueryConversationCard: React.FC = () => {
  const {
    messages,
    prevChannelId,
    scrolledBottom,
    setScrolledBottom,
    groupedMessages,
    initMessagesLoaded,
    fetchHistory,
    fetchingMessages,
    goToBottom,
    toBottom,
    allPaginationEnded,
    channelId,
    hasNewMessages,
    conversationId,
    growQueryConversationThread,
  } = useGetConversationHistory();

  const [loadMore, setLoadMore] = useState(false);
  const [loadMoreBottom, setLoadMoreBottom] = useState(false);
  const [growBottomPaginationEnded, setGrowBottomPaginationEnded] =
    useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const initialGrowAlignmentKeyRef = useRef<string | null>(null);
  const suppressNextAutoScrollRef = useRef(false);

  const focusedMessageRef = useRef<string | null>(null);
  const lastItemRef = useRef<HTMLDivElement | null>(null);
  const growSeparatorElementsRef = useRef<Map<string, HTMLDivElement>>(
    new Map()
  );
  const messageElementsRef = useRef<Map<string, HTMLDivElement>>(new Map());

  const paginationAnchorMessageId = useMemo(() => {
    for (let i = groupedMessages.length - 1; i >= 0; i--) {
      const item = groupedMessages[i];
      if (!isMessageEnvelopeItem(item)) {
        continue;
      }

      const messageId = item.message?._id;
      if (messageId !== undefined && messageId !== null) {
        return String(messageId);
      }
    }

    for (let i = groupedMessages.length - 1; i >= 0; i--) {
      const item = groupedMessages[i];
      if (isMessageEnvelopeItem(item)) {
        const messageId = item.message?._id;
        if (messageId !== undefined && messageId !== null) {
          return String(messageId);
        }
      }
    }

    return undefined;
  }, [groupedMessages]);

  const latestMessageTimetoken = useMemo(() => {
    const latestMessage = messages[messages.length - 1];
    const timetoken = Number(latestMessage?.timetoken);

    return Number.isNaN(timetoken) ? undefined : String(timetoken);
  }, [messages]);

  const firstGrowStartKey = useMemo(() => {
    for (let i = 0; i < groupedMessages.length; i++) {
      const item = groupedMessages[i];
      if (isQuerySeparatorItem(item) && item.boundary === 'start') {
        return getMessageListItemKey(item, i);
      }
    }

    return undefined;
  }, [groupedMessages]);

  const scrollNodeIntoView = useCallback(
    (node: HTMLDivElement, behavior: ScrollBehavior) => {
      if (!listRef.current) return;

      const parent = listRef.current;
      const parentHeight = parent.clientHeight;
      const itemTop = node.offsetTop;
      const itemHeight = node.clientHeight;

      const scrollPosition = itemTop - parentHeight / 2 + itemHeight / 2;
      parent.scrollTo({
        top: scrollPosition,
        behavior,
      });
    },
    []
  );

  const scrollToItem = useCallback(
    (key: string) => {
      const item = messageElementsRef.current.get(key);
      if (item) {
        scrollNodeIntoView(item, 'smooth');
      }
    },
    [scrollNodeIntoView]
  );

  const scrollToGrowSeparator = useCallback(
    (key: string, behavior: ScrollBehavior) => {
      const separator = growSeparatorElementsRef.current.get(key);
      if (separator) {
        scrollNodeIntoView(separator, behavior);
      }
    },
    [scrollNodeIntoView]
  );

  const scrollToBottom = useCallback(() => {
    if (listRef.current) {
      listRef.current.scrollTo({
        // In column-reverse, visual bottom maps to scrollTop near 0.
        top: 0,
        behavior: 'smooth',
      });
      toBottom();
    }
  }, [toBottom]);

  const handleScroll = useCallback(() => {
    const container = listRef.current;
    if (!container) return;

    // In column-reverse, visual bottom maps to scrollTop near 0.
    const isAtBottom = container.scrollTop <= 8;
    setScrolledBottom((prev) => (prev === isAtBottom ? prev : isAtBottom));

    if (
      !allPaginationEnded &&
      !fetchingMessages &&
      !loadMore &&
      hasReachedPaginationAnchor()
    ) {
      setLoadMore(true);
    }

    if (
      growQueryConversationThread?.isGrowConversation &&
      !growBottomPaginationEnded &&
      !fetchingMessages &&
      !loadMoreBottom &&
      isAtBottom
    ) {
      setLoadMoreBottom(true);
    }
  }, [
    allPaginationEnded,
    fetchingMessages,
    growBottomPaginationEnded,
    growQueryConversationThread?.isGrowConversation,
    loadMore,
    loadMoreBottom,
    setScrolledBottom,
  ]);

  const hasReachedPaginationAnchor = useCallback(() => {
    const container = listRef.current;
    const anchor = lastItemRef.current;
    if (!container || !anchor) return false;

    const containerRect = container.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();

    // In column-reverse, load next page only when the anchor enters the visual top band.
    return (
      anchorRect.top >= containerRect.top - 24 &&
      anchorRect.top <= containerRect.top + 24
    );
  }, []);

  useEffect(() => {
    const fetchMoreHistory = async () => {
      if (loadMore && !fetchingMessages) {
        suppressNextAutoScrollRef.current = true;
        await fetchHistory();
        setLoadMore(false);
      }

      if (
        loadMoreBottom &&
        !fetchingMessages &&
        growQueryConversationThread?.isGrowConversation &&
        latestMessageTimetoken
      ) {
        suppressNextAutoScrollRef.current = true;
        const fetchedCount = await fetchHistory({
          endTimeToken: latestMessageTimetoken,
        });
        if (!isNil(fetchedCount) && fetchedCount < 25) {
          setGrowBottomPaginationEnded(true);
        }
        setLoadMoreBottom(false);
      }
    };

    if ((loadMore || loadMoreBottom) && !initMessagesLoaded[channelId]) return;
    fetchMoreHistory();
  }, [
    initMessagesLoaded,
    channelId,
    loadMore,
    loadMoreBottom,
    fetchingMessages,
    fetchHistory,
    growQueryConversationThread?.isGrowConversation,
    latestMessageTimetoken,
  ]);

  useEffect(() => {
    setLoadMoreBottom(false);
    setGrowBottomPaginationEnded(false);
  }, [channelId, growQueryConversationThread?.conversationId]);

  useEffect(() => {
    if (!listRef.current || fetchingMessages) return;
    if (!growQueryConversationThread?.isGrowConversation) return;
    if (!firstGrowStartKey) return;

    const alignmentKey =
      growQueryConversationThread.conversationId || channelId;
    if (initialGrowAlignmentKeyRef.current === alignmentKey) return;

    suppressNextAutoScrollRef.current = true;
    scrollToGrowSeparator(firstGrowStartKey, 'auto');
    setScrolledBottom(false);
    initialGrowAlignmentKeyRef.current = alignmentKey;
  }, [
    channelId,
    firstGrowStartKey,
    fetchingMessages,
    growQueryConversationThread?.conversationId,
    growQueryConversationThread?.isGrowConversation,
    setScrolledBottom,
    scrollToGrowSeparator,
  ]);

  useEffect(() => {
    if (goToBottom) scrollToBottom();
    if (prevChannelId !== channelId) scrollToBottom();
    if (!scrolledBottom) return;
    if (!hasNewMessages) return;
    if (suppressNextAutoScrollRef.current) {
      suppressNextAutoScrollRef.current = false;
      return;
    }
    scrollToBottom();
  }, [
    channelId,
    goToBottom,
    hasNewMessages,
    prevChannelId,
    scrollToBottom,
    scrolledBottom,
  ]);

  useEffect(() => {
    if (listRef.current && !fetchingMessages && !loadMore) {
      const { scrollHeight, clientHeight } = listRef.current;
      const lastItemVisible = hasReachedPaginationAnchor();

      if (
        !allPaginationEnded &&
        scrollHeight <= clientHeight &&
        groupedMessages.length > 0 &&
        lastItemVisible
      ) {
        setLoadMore(true);
      }
    }
  }, [groupedMessages, fetchingMessages, loadMore, allPaginationEnded]);

  const handleSendMsg = async () => {
    await fetchHistory({
      endTimeToken: latestMessageTimetoken,
    });
  };

  return (
    <div className="query-conversation-card">
      {!fetchingMessages && isEmpty(groupedMessages) ? (
        <EmptyStateView
          imageSrc={SVGS.NoConversation}
          message={'No conversations found'}
          description={'Try changing the date to view conversations.'}
        />
      ) : fetchingMessages && isEmpty(groupedMessages) ? (
        <div className="query-conversation-loading-view">
          <CircularProgress size={25} />
          <div className="query-conversation-loading-view__text font-medium">
            {'Messages Loading...'}
          </div>
        </div>
      ) : (
        <div
          ref={listRef}
          className="query-conversation-message-view"
          onScroll={handleScroll}
        >
          {groupedMessages.map((message, index) => {
            return (
              <QueryMessageList
                key={getMessageListItemKey(message, index)}
                messageItem={message}
                messageItemKey={getMessageListItemKey(message, index)}
                paginationAnchorMessageId={paginationAnchorMessageId}
                lastItemRef={lastItemRef}
                growSeparatorElementsRef={growSeparatorElementsRef}
                messageElementsRef={messageElementsRef}
                scrollToItem={scrollToItem}
              />
            );
          })}
        </div>
      )}

      <QueryMessageInput
        channelId={String(channelId)}
        conversationId={String(conversationId)}
        handleSendMsg={handleSendMsg}
      />
    </div>
  );
};
